import { AdminTenant } from '@/hooks/useAdminTenants'
import { useImpersonation } from '@/hooks/useImpersonation'
import {
  useApproveTenant,
  useSuspendTenant,
  useReactivateTenant,
} from '@/hooks/useAdminTenants'

interface TenantOverviewCardProps {
  tenant: AdminTenant
}

export default function TenantOverviewCard({ tenant }: TenantOverviewCardProps) {
  const { impersonate, isStarting } = useImpersonation()
  const approveMutation = useApproveTenant()
  const suspendMutation = useSuspendTenant()
  const reactivateMutation = useReactivateTenant()

  const handleAction = async (action: 'approve' | 'suspend' | 'reactivate') => {
    const confirmMessages = {
      approve: `Approve "${tenant.name}"?`,
      suspend: `Suspend "${tenant.name}"?`,
      reactivate: `Reactivate "${tenant.name}"?`,
    }

    if (!confirm(confirmMessages[action])) return

    try {
      switch (action) {
        case 'approve':
          await approveMutation.mutateAsync(tenant.id)
          break
        case 'suspend':
          await suspendMutation.mutateAsync(tenant.id)
          break
        case 'reactivate':
          await reactivateMutation.mutateAsync(tenant.id)
          break
      }
    } catch (err: any) {
      alert(err.response?.data?.detail || `Failed to ${action} tenant`)
    }
  }

  const handleImpersonate = async () => {
    try {
      await impersonate(tenant.id)
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Failed to start impersonation')
    }
  }

  return (
    <div className="card space-y-6">
      {/* Basic Info */}
      <div className="grid grid-cols-2 gap-6">
        <div>
          <h3 className="text-sm font-medium text-gray-500">Organization Name</h3>
          <p className="mt-1 text-lg font-semibold text-gray-900">{tenant.name}</p>
        </div>
        <div>
          <h3 className="text-sm font-medium text-gray-500">Slug</h3>
          <p className="mt-1 text-lg text-gray-900">{tenant.slug}</p>
        </div>
        <div>
          <h3 className="text-sm font-medium text-gray-500">Plan</h3>
          <p className="mt-1 text-lg text-gray-900 uppercase font-semibold">{tenant.plan}</p>
        </div>
        <div>
          <h3 className="text-sm font-medium text-gray-500">Status</h3>
          <StatusBadge status={tenant.status} />
        </div>
        <div>
          <h3 className="text-sm font-medium text-gray-500">Admin Contact</h3>
          <p className="mt-1 text-gray-900">{tenant.admin_name || '-'}</p>
          <p className="text-sm text-gray-500">{tenant.admin_email || '-'}</p>
        </div>
        <div>
          <h3 className="text-sm font-medium text-gray-500">Users</h3>
          <p className="mt-1 text-lg text-gray-900">{tenant.user_count}</p>
        </div>
        <div>
          <h3 className="text-sm font-medium text-gray-500">Created</h3>
          <p className="mt-1 text-gray-900">
            {new Date(tenant.created_at).toLocaleDateString()}
          </p>
        </div>
        <div>
          <h3 className="text-sm font-medium text-gray-500">Last Login</h3>
          <p className="mt-1 text-gray-900">
            {tenant.last_login ? new Date(tenant.last_login).toLocaleDateString() : 'Never'}
          </p>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="border-t pt-6">
        <h3 className="text-sm font-medium text-gray-700 mb-4">Quick Actions</h3>
        <div className="flex gap-3">
          {tenant.status === 'active' && (
            <>
              <button
                onClick={handleImpersonate}
                disabled={isStarting}
                className="btn-secondary"
              >
                Impersonate
              </button>
              <button
                onClick={() => handleAction('suspend')}
                disabled={suspendMutation.isPending}
                className="btn-secondary"
              >
                Suspend
              </button>
            </>
          )}
          {tenant.status === 'pending' && (
            <button
              onClick={() => handleAction('approve')}
              disabled={approveMutation.isPending}
              className="btn-primary"
            >
              Approve
            </button>
          )}
          {tenant.status === 'suspended' && (
            <button
              onClick={() => handleAction('reactivate')}
              disabled={reactivateMutation.isPending}
              className="btn-primary"
            >
              Reactivate
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

function StatusBadge({ status }: { status: string }) {
  const styles: Record<string, string> = {
    pending: 'bg-yellow-100 text-yellow-800',
    active: 'bg-green-100 text-green-800',
    suspended: 'bg-red-100 text-red-800',
    cancelled: 'bg-gray-100 text-gray-800',
  }

  return (
    <span
      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize mt-1 ${
        styles[status] || styles.active
      }`}
    >
      {status}
    </span>
  )
}
